import { api, type IncomeRecord, queryKeys } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatLds, formatNumber, formatUsd } from '../../lib/format';
import { Card, Spinner, Table } from '../../ui';
import { INCOME_TABS } from './incomeTabs';

const col = createColumnHelper<IncomeRecord>();

export function LevelIncome() {
  const { query, setPage } = useTableQuery({ extraParams: ['level'] });

  const breakdown = useQuery({
    queryKey: ['level-breakdown'],
    queryFn: () => api.income.levelBreakdown().then((r) => r.data),
  });

  const ledger = useQuery({
    queryKey: queryKeys.income('level_income', query),
    queryFn: () => api.income.list('level_income', query),
  });

  const columns = useMemo(
    () => [
      col.accessor('level', { header: 'Level' }),
      col.accessor('from_user_code', { header: 'From', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('amount_usd', { header: 'USD', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('amount_lds', { header: 'LDS', cell: (i) => formatLds(i.getValue()) }),
      col.accessor('created_at', { header: 'Credited', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Level Income"
        description="Seven levels of the sponsor tree. Each qualifying activation pays up the chain."
        tabs={INCOME_TABS}
      />

      <Card>
        <h3 className="card-title">Level breakdown</h3>
        {breakdown.isLoading ? (
          <Spinner />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Level</th>
                <th>Rate</th>
                <th>Members</th>
                <th>Active</th>
                <th>Earned</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.data?.map((row) => (
                <tr key={row.level}>
                  <td>Level {row.level}</td>
                  <td>
                    {formatUsd(row.rate_usd)} <span className="text-muted small">({formatLds(row.rate_lds)})</span>
                  </td>
                  <td>{formatNumber(row.members)}</td>
                  <td>{formatNumber(row.active_members)}</td>
                  <td className="text-gold">
                    {formatUsd(row.earned_usd)}{' '}
                    <span className="text-muted small">({formatLds(row.earned_lds)})</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <DataTable<IncomeRecord>
        title="Level Income Ledger"
        columns={columns}
        data={ledger.data?.data}
        meta={ledger.data?.meta}
        isLoading={ledger.isLoading}
        error={ledger.error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
